import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Clock, Award, Send } from "lucide-react";
import { BrandBadge } from "@/components/BrandBadge";
import QuickDemoDialog from "@/components/QuickDemoDialog";
import { fetchCourses } from "@/lib/api";

const VENDORS = ["Salesforce", "AWS", "Mulesoft", "IBM"];

export default function CoursesPage() {
  const [courses, setCourses] = useState([]);
  const [params, setParams] = useSearchParams();
  const initialVendor = params.get("vendor") && VENDORS.includes(params.get("vendor")) ? params.get("vendor") : "Salesforce";
  const [tab, setTab] = useState(initialVendor);

  useEffect(() => {
    fetchCourses().then(setCourses).catch(() => setCourses([]));
  }, []);

  useEffect(() => {
    setParams({ vendor: tab }, { replace: true });
  }, [tab]); // eslint-disable-line

  const grouped = useMemo(() => {
    return VENDORS.reduce((acc, v) => {
      acc[v] = courses.filter((c) => c.vendor === v);
      return acc;
    }, {});
  }, [courses]);

  return (
    <div data-testid="courses-page" className="bg-white">
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-20">
          <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Course catalog</div>
          <h1 className="mt-3 font-display font-bold tracking-tighter text-4xl md:text-5xl lg:text-6xl text-slate-950 max-w-3xl">
            Every course. Every vendor. One fee.
          </h1>
          <p className="mt-5 text-slate-600 text-base md:text-lg max-w-2xl">
            Course fees start from <span className="font-semibold text-slate-950">INR 19,999 + GST</span> (India) or <span className="font-semibold text-slate-950">USD 499</span> (Global).
            Weekend batches, live projects, and 1:1 mentoring included.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-14 md:py-20">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100 p-1 h-auto flex flex-wrap gap-1 justify-start">
            {VENDORS.map((v) => {
              return (
                <TabsTrigger
                  key={v}
                  value={v}
                  data-testid={`courses-tab-${v.toLowerCase()}`}
                  className="data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-sm px-4 py-2 flex items-center gap-2"
                >
                  <BrandBadge vendor={v} size={22} />
                  <span className="text-sm font-medium">{v}</span>
                  <span className="text-xs text-slate-500">({grouped[v]?.length || 0})</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {VENDORS.map((v) => (
            <TabsContent key={v} value={v} className="mt-10">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(grouped[v] || []).map((c) => (
                  <div
                    key={c.id}
                    data-testid={`course-card-${c.id}`}
                    className="group border border-slate-200 bg-white p-6 flex flex-col hover:-translate-y-1 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <span className="text-[11px] uppercase tracking-widerest font-mono-plex text-slate-500">{c.level}</span>
                      <span className="text-[11px] uppercase tracking-widerest font-mono-plex text-[#0052FF]">{c.vendor}</span>
                    </div>
                    <h3 className="mt-4 font-display font-semibold text-xl tracking-tight text-slate-950">{c.title}</h3>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{c.duration}</span>
                      <span className="inline-flex items-center gap-1"><Award className="w-3.5 h-3.5" />{c.certification}</span>
                    </div>
                    <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                      {c.highlights.map((h, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-[#0052FF] shrink-0">›</span>{h}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 pt-4 border-t border-slate-100 flex items-end justify-between gap-2">
                      <div>
                        <div className="text-[11px] uppercase tracking-widerest text-slate-500 font-mono-plex">Fee</div>
                        <div className="font-display font-bold text-slate-950">
                          ₹{c.price_inr.toLocaleString("en-IN")}<span className="text-xs font-normal text-slate-500"> + GST (India)</span>
                          <div className="text-xs font-normal text-slate-500 mt-0.5">${c.price_usd} (Global)</div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <Link
                          to={`/pay-demo?course=${encodeURIComponent(c.title)}`}
                          data-testid={`course-pay-demo-${c.id}`}
                          className="inline-flex items-center gap-1.5 bg-[#0052FF] hover:bg-[#0040CC] text-white text-sm font-medium px-3.5 py-2 transition-colors"
                        >
                          <Send className="w-3.5 h-3.5" /> Pay ₹500 & book demo
                        </Link>
                        <QuickDemoDialog course={c.title} testIdPrefix={`course-${c.id}-quick-demo`}>
                          <button
                            type="button"
                            data-testid={`course-contact-demo-${c.id}`}
                            className="text-xs text-slate-600 hover:text-[#0052FF] underline underline-offset-2"
                          >
                            or ask for pricing
                          </button>
                        </QuickDemoDialog>
                      </div>
                    </div>
                  </div>
                ))}
                {(grouped[v] || []).length === 0 && (
                  <div className="col-span-full text-slate-500 text-sm">Loading courses…</div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>
    </div>
  );
}
